import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { openConversation } from "@/components/Conversation";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Recruitment", href: "#recruitment" },
  { label: "Advisory", href: "#advisory" },
  { label: "Pre-Opening", href: "#pre-opening" },
  { label: "Employers", href: "#employers" },
  { label: "Candidates", href: "#candidates" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 350 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="main-navigation"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled
            ? "bg-ivory/90 backdrop-blur-xl border-b border-ink/10 py-4"
            : "bg-transparent py-7"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => go("#home")}
            className="text-left group flex items-center gap-3"
          >
            <img
              src="/images/br-logo-official.jpg"
              alt="BR Hospitality monogram"
              className="w-9 h-9 object-cover"
            />
            <span>
            <span
              className={`block font-serif text-xl tracking-[0.18em] transition-colors duration-700 ${
                scrolled ? "text-ink" : "text-ivory"
              }`}
            >
              BR HOSPITALITY
            </span>
            <span
              className={`block text-[9px] tracking-[0.4em] uppercase transition-colors duration-700 ${
                scrolled ? "text-bronze" : "text-bronze"
              }`}
            >
              Recruitment &amp; Advisory
            </span>
            </span>
          </button>

          <nav className="hidden xl:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace("-", "")}`}
                onClick={() => go(l.href)}
                className={`link-underline text-[11px] tracking-[0.22em] uppercase transition-colors duration-700 ${
                  scrolled ? "text-ink/70 hover:text-ink" : "text-ivory/80 hover:text-ivory"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="nav-cta-build-team"
              onClick={openConversation}
              className={`hidden md:inline-block text-[11px] tracking-[0.25em] uppercase px-6 py-3 border transition-all duration-500 ${
                scrolled
                  ? "border-ink text-ink hover:bg-ink hover:text-ivory"
                  : "border-ivory/50 text-ivory hover:bg-ivory hover:text-ink"
              }`}
            >
              Let&apos;s Build Your Team
            </button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className={`xl:hidden transition-colors duration-700 ${
                scrolled || open ? "text-ink" : "text-ivory"
              }`}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-ivory flex flex-col justify-center px-8"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace("-", "")}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }}
                onClick={() => go(l.href)}
                className="text-left font-serif text-4xl text-ink py-3 border-b border-ink/10 hover:text-bronze transition-colors duration-300"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              data-testid="nav-mobile-cta-build-team"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={() => {
                setOpen(false);
                openConversation();
              }}
              className="mt-8 text-[11px] tracking-[0.25em] uppercase px-6 py-4 border border-ink text-ink hover:bg-ink hover:text-ivory transition-all duration-500"
            >
              Let&apos;s Build Your Team
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
